import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { AlertTriangle, ArrowDown, ArrowRight, ArrowUp, Info, X } from 'lucide-react';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { formatMoneda } from '../state/format';
import { formatVariacion } from '../state/formatVersiones';
import { ReprecioResumen } from '../types';

const texto = { fontFamily: 'Poppins, sans-serif' };
const MAXIMO_LINEAS = 8;

const MOTIVOS: Record<ReprecioResumen['marcacion_conservada'][number]['motivo'], string> = {
  ESQUEMA_NO_RECALCULABLE: 'tarifa no recalculable',
  SIN_OPCION_APLICABLE: 'sin tarifa para esta cantidad',
  OPCION_INEXISTENTE: 'la tarifa ya no existe',
};

interface ReprecioResumenModalProps {
  reprecio: ReprecioResumen;
  titulo?: string;
  subtitulo?: string;
  onCerrar: () => void;
  onRevisar?: () => void;
}

// SPEC-206 (RN-007, CA-008, design §7b) — resumen del re-precio tras reabrir (drawer o arrastre a
// Borrador) o duplicar: total antes → después, líneas que cambiaron y las que se revisan a mano.
export const ReprecioResumenModal: React.FC<ReprecioResumenModalProps> = ({
  reprecio,
  titulo = 'Precios actualizados al catálogo de hoy',
  subtitulo,
  onCerrar,
  onRevisar,
}) => {
  const actualizadas = reprecio.lineas_actualizadas;
  const variacionTotal = reprecio.total_anterior !== 0 ? (reprecio.diferencia / reprecio.total_anterior) * 100 : null;

  return (
    <Dialog open onClose={onCerrar} aria-labelledby="reprecio-titulo" fullWidth maxWidth="xs" data-testid="reprecio-resumen-modal">
      <DialogTitle id="reprecio-titulo" sx={{ ...texto, fontSize: fs(17), fontWeight: 700, color: TOKENS.ink, paddingRight: '48px' }}>
        {titulo}
        <IconButton aria-label="Cerrar" onClick={onCerrar} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {subtitulo && <Typography sx={{ ...texto, fontSize: fs(12.5), color: TOKENS.muted, marginBottom: '12px' }}>{subtitulo}</Typography>}

        <Box data-testid="reprecio-totales" sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <Box sx={{ border: `1px solid ${TOKENS.hair}`, borderRadius: '10px', padding: '6px 10px' }}>
            <Typography sx={{ ...texto, fontSize: fs(13), fontWeight: 600, color: TOKENS.text }}>{formatMoneda(reprecio.total_anterior)}</Typography>
          </Box>
          <ArrowRight size={16} aria-label="pasa a" />
          <Box sx={{ border: `1px solid ${TOKENS.purpleSoft}`, borderRadius: '10px', padding: '6px 10px' }}>
            <Typography sx={{ ...texto, fontSize: fs(13), fontWeight: 700, color: TOKENS.ink }}>{formatMoneda(reprecio.total_nuevo)}</Typography>
          </Box>
          {variacionTotal !== null && (
            <Typography sx={{ ...texto, fontSize: fs(12), fontWeight: 600, color: TOKENS.text }}>{formatVariacion(Math.round(variacionTotal * 10) / 10)}</Typography>
          )}
        </Box>

        {actualizadas.length === 0 ? (
          <Typography sx={{ ...texto, fontSize: fs(12.5), color: TOKENS.text }}>Los precios del catálogo no cambiaron</Typography>
        ) : (
          <Box>
            <Typography sx={{ ...texto, fontSize: fs(12.5), fontWeight: 600, color: TOKENS.ink, marginBottom: '6px' }}>
              {actualizadas.length === 1 ? '1 producto cambió de precio' : `${actualizadas.length} productos cambiaron de precio`}
            </Typography>
            {actualizadas.slice(0, MAXIMO_LINEAS).map((linea) => {
              const sube = linea.precio_venta_nuevo > linea.precio_venta_anterior;
              const Flecha = sube ? ArrowUp : ArrowDown;
              return (
                <Box key={linea.linea_id} sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '2px 0' }}>
                  <Typography sx={{ ...texto, fontSize: fs(12), color: TOKENS.text }}>{linea.nombre_producto}</Typography>
                  <Typography sx={{ ...texto, fontSize: fs(12), color: TOKENS.ink, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    {formatMoneda(linea.precio_venta_anterior)} → {formatMoneda(linea.precio_venta_nuevo)}
                    <Flecha size={12} aria-label={sube ? 'sube' : 'baja'} />
                    {linea.variacion_pct !== null ? formatVariacion(linea.variacion_pct) : ''}
                  </Typography>
                </Box>
              );
            })}
            {actualizadas.length > MAXIMO_LINEAS && (
              <Typography sx={{ ...texto, fontSize: fs(12), color: TOKENS.muted }}>y {actualizadas.length - MAXIMO_LINEAS} más</Typography>
            )}
          </Box>
        )}

        {reprecio.sin_precio_vigente.length > 0 && (
          <Box data-testid="reprecio-sin-precio" sx={{ marginTop: '12px', padding: '8px 10px', borderRadius: '10px', backgroundColor: 'rgba(201,138,27,.10)' }}>
            <Typography sx={{ ...texto, fontSize: fs(12), fontWeight: 600, color: TOKENS.ink, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AlertTriangle size={14} color={TOKENS.amber} aria-hidden /> Sin precio vigente (se conservó el anterior)
            </Typography>
            {reprecio.sin_precio_vigente.map((l) => (
              <Typography key={l.linea_id} sx={{ ...texto, fontSize: fs(12), color: TOKENS.text }}>{l.nombre_producto}</Typography>
            ))}
          </Box>
        )}

        {reprecio.marcacion_conservada.length > 0 && (
          <Box data-testid="reprecio-marcacion-conservada" sx={{ marginTop: '10px', padding: '8px 10px', borderRadius: '10px', backgroundColor: 'rgba(59,111,201,.08)' }}>
            <Typography sx={{ ...texto, fontSize: fs(12), fontWeight: 600, color: TOKENS.ink, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Info size={14} color={TOKENS.blue} aria-hidden /> Marcación conservada — revísala a mano
            </Typography>
            {reprecio.marcacion_conservada.map((l) => (
              <Typography key={l.linea_id} sx={{ ...texto, fontSize: fs(12), color: TOKENS.text }}>{l.nombre_producto} · {MOTIVOS[l.motivo]}</Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 18px' }}>
        <Button onClick={onCerrar} sx={{ ...texto, textTransform: 'none', color: TOKENS.text }}>Cerrar</Button>
        {onRevisar && (
          <Button
            onClick={onRevisar}
            sx={{ ...texto, textTransform: 'none', backgroundColor: TOKENS.purple, color: '#fff', borderRadius: '999px', padding: '6px 16px', '&:hover': { backgroundColor: TOKENS.purpleDark } }}
          >
            Revisar cotización →
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
